import { Component } from '@theme/component';

/**
 * A centered, overlay modal shown once per visitor.
 *
 * On first connect it checks localStorage for a dismissal flag; if absent it
 * reveals itself. Closing it (button, overlay click, Escape, or the CTA) sets
 * the flag so it never reappears. Starts `hidden` in the markup so it never
 * flashes before this module runs (and stays hidden if JS fails to load).
 *
 * @extends {Component}
 */
class AnnouncementModal extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.storageKey = this.dataset.storageKey || 'announcement-modal';

    // Only show when the visitor arrived from the old domain, and only once ever.
    if (!this.#cameFromReferrer()) return;

    let dismissed = false;
    try {
      dismissed = localStorage.getItem(this.storageKey) === '1';
    } catch (e) {
      // localStorage unavailable (private mode / blocked) — show once this session
    }

    if (dismissed) return;

    this.hidden = false;
    document.body.classList.add('announcement-modal-open');
    document.addEventListener('keydown', this.#onKeydown);
  }

  /**
   * True if the visitor was referred from the configured host, or arrived with a
   * matching `?from=`/`?ref=` query param (a fallback for redirects, which often
   * strip the referrer).
   *
   * @returns {boolean}
   */
  #cameFromReferrer() {
    const host = (this.dataset.referrerHost || '').toLowerCase();
    if (!host) return true; // no host configured → show on any first landing

    try {
      const referrer = document.referrer;
      if (referrer) {
        const referrerHost = new URL(referrer).hostname.toLowerCase();
        if (referrerHost === host || referrerHost.endsWith('.' + host)) return true;
      }
    } catch (e) {
      // malformed referrer — fall through to the query-param check
    }

    try {
      const params = new URLSearchParams(window.location.search);
      const from = (params.get('from') || params.get('ref') || '').toLowerCase();
      // Match the full host ("digital-microbiology.com") or its base label
      // ("digital-microbiology"), so ?from=digital-microbiology works too.
      const base = host.split('.')[0];
      if (from && (from.includes(host) || (base && from.includes(base)))) return true;
    } catch (e) {
      // ignore
    }

    return false;
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    document.removeEventListener('keydown', this.#onKeydown);
    document.body.classList.remove('announcement-modal-open');
  }

  /** Hide the modal and persist the dismissal so it never shows again. */
  close = () => {
    this.hidden = true;
    document.body.classList.remove('announcement-modal-open');
    document.removeEventListener('keydown', this.#onKeydown);
    try {
      localStorage.setItem(this.storageKey, '1');
    } catch (e) {
      // ignore write failures
    }
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key === 'Escape') this.close();
  };
}

if (!customElements.get('announcement-modal')) {
  customElements.define('announcement-modal', AnnouncementModal);
}
